import { useEffect, useRef, useState } from "react";
import {
  Sparkles, Send, Check, X, Loader2, ArrowRight, DollarSign, Clock, Wand2, AlertTriangle,
} from "lucide-react";
import api from "@/lib/api";
import { toast } from "sonner";
import { GlassCard, Pill, cx } from "@/components/shared";

const SUGGESTIONS = [
  "Replace Scene 2 with a fresh take",
  "Make Scene 1 brighter",
  "Slow the narration",
  "Use a happier female voice",
  "Reduce background music",
  "Make the ending stronger",
  "Replace thumbnail",
];

export default function AIReview({ pid, onChange }) {
  const [instruction, setInstruction] = useState("");
  const [analyzing, setAnalyzing] = useState(false);
  const [plan, setPlan] = useState(null);
  const [applying, setApplying] = useState(false);
  const [status, setStatus] = useState(null);
  const timer = useRef(null);

  useEffect(() => () => clearTimeout(timer.current), []);

  const analyze = async (text) => {
    const q = (text ?? instruction).trim();
    if (!q) return;
    setInstruction(q);
    setPlan(null);
    setAnalyzing(true);
    try {
      const { data } = await api.post(`/projects/${pid}/ai-review/analyze`, { instruction: q });
      if (!data.actions?.length) {
        toast.error("Couldn't map that to an edit — try rephrasing");
      }
      setPlan(data);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Analysis failed");
    } finally {
      setAnalyzing(false);
    }
  };

  const pollStatus = async () => {
    try {
      const { data } = await api.get(`/projects/${pid}/ai-review/status`);
      setStatus(data);
      if (data.status === "running") {
        timer.current = setTimeout(pollStatus, 3000);
      } else {
        setApplying(false);
        onChange && onChange();
        if (data.status === "done") toast.success("Changes applied — preview updated");
      }
    } catch { setApplying(false); }
  };

  const apply = async () => {
    if (!plan?.actions?.length) return;
    setApplying(true);
    try {
      await api.post(`/projects/${pid}/ai-review/apply`, { actions: plan.actions });
      toast.success("Applying only the required changes…");
      setPlan(null);
      setInstruction("");
      pollStatus();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not apply");
      setApplying(false);
    }
  };

  return (
    <div className="space-y-4" data-testid="ai-review">
      <GlassCard className="p-6">
        <div className="flex items-center gap-3 mb-4">
          <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-[#5B4DFF] to-[#FFB300] grid place-items-center">
            <Wand2 className="w-5 h-5 text-white" />
          </div>
          <div>
            <h3 className="font-display font-semibold text-white">AI Review & Editing</h3>
            <p className="text-xs text-slate-400">Talk to Vyom in plain language. It regenerates only what needs to change.</p>
          </div>
        </div>

        <div className="flex gap-2">
          <input
            value={instruction}
            onChange={(e) => setInstruction(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && analyze()}
            placeholder="e.g. Replace Scene 3 and use a female narrator"
            data-testid="ai-review-input"
            className="flex-1 bg-white/5 border border-white/10 rounded-xl px-4 py-3 outline-none text-sm text-white placeholder:text-slate-500 focus:border-[#5B4DFF]/60"
          />
          <button onClick={() => analyze()} disabled={analyzing || applying} data-testid="ai-review-analyze"
            className="inline-flex items-center gap-2 px-4 py-3 rounded-xl bg-[#5B4DFF] text-white text-sm font-medium disabled:opacity-50 hover:bg-[#6d5fff]">
            {analyzing ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />} Analyze
          </button>
        </div>

        <div className="flex flex-wrap gap-2 mt-3">
          {SUGGESTIONS.map((s) => (
            <button key={s} onClick={() => analyze(s)} disabled={analyzing || applying}
              className="text-[11px] px-2.5 py-1 rounded-full bg-white/5 border border-white/10 text-slate-400 hover:text-white hover:border-[#5B4DFF]/40 disabled:opacity-50">
              {s}
            </button>
          ))}
        </div>
      </GlassCard>

      {analyzing && (
        <GlassCard className="p-6 text-center text-slate-400 flex items-center justify-center gap-3">
          <Loader2 className="w-5 h-5 animate-spin text-[#5B4DFF]" /> Understanding your request…
        </GlassCard>
      )}

      {plan && !analyzing && (
        <GlassCard className="p-6 space-y-5" data-testid="ai-review-plan">
          <div>
            <div className="font-mono text-[11px] uppercase tracking-wider text-[#FFB300] mb-1">Understood</div>
            <p className="text-slate-200 text-sm flex items-center gap-2"><Sparkles className="w-4 h-4 text-[#5B4DFF]" /> {plan.understood}</p>
          </div>

          {plan.actions?.length ? (
            <>
              <div className="grid sm:grid-cols-2 gap-4">
                <div>
                  <div className="font-mono text-[11px] uppercase tracking-wider text-emerald-400 mb-2">Will be regenerated</div>
                  <ul className="space-y-1.5">
                    {plan.actions.map((act, i) => (
                      <li key={i} className="text-sm text-slate-200 flex items-center gap-2" data-testid="ai-review-change">
                        <Check className="w-4 h-4 text-emerald-400 shrink-0" /> {act.label || act.type}
                        {act.skipped && <Pill tone="gold" className="ml-1">skipped · {act.skipped}</Pill>}
                      </li>
                    ))}
                  </ul>
                </div>
                <div>
                  <div className="font-mono text-[11px] uppercase tracking-wider text-slate-400 mb-2">Will stay unchanged</div>
                  <ul className="space-y-1.5">
                    {plan.unchanged.map((u, i) => (
                      <li key={i} className="text-sm text-slate-400 flex items-center gap-2"><Check className="w-4 h-4 text-slate-500 shrink-0" /> {u}</li>
                    ))}
                  </ul>
                </div>
              </div>

              <div className="flex flex-wrap items-center gap-4 pt-2 border-t border-white/5">
                <span className="text-sm text-slate-300 flex items-center gap-1.5" data-testid="ai-review-cost">
                  <DollarSign className="w-4 h-4 text-[#FFB300]" /> Est. cost: <b className="text-white">${plan.estimated_cost_usd}</b>
                  <span className="text-slate-500">(≈ ₹{plan.estimated_cost_inr})</span>
                </span>
                <span className="text-sm text-slate-300 flex items-center gap-1.5">
                  <Clock className="w-4 h-4 text-[#5B4DFF]" /> Est. time: <b className="text-white">~{Math.round(plan.estimated_time_seconds / 6) / 10} min</b>
                </span>
                {!plan.video_provider && plan.actions.some((a) => a.type === "scene_video") && (
                  <span className="text-xs text-[#FFB300] flex items-center gap-1"><AlertTriangle className="w-3.5 h-3.5" /> Video provider offline — clip re-renders will skip</span>
                )}
              </div>

              <div className="flex gap-2">
                <button onClick={apply} disabled={applying} data-testid="ai-review-apply"
                  className="inline-flex items-center gap-2 px-5 py-2.5 rounded-xl bg-emerald-500 text-white text-sm font-medium hover:bg-emerald-400 disabled:opacity-50">
                  {applying ? <Loader2 className="w-4 h-4 animate-spin" /> : <ArrowRight className="w-4 h-4" />} Proceed
                </button>
                <button onClick={() => setPlan(null)} data-testid="ai-review-cancel"
                  className="inline-flex items-center gap-2 px-5 py-2.5 rounded-xl bg-white/5 border border-white/10 text-slate-300 text-sm hover:text-white">
                  <X className="w-4 h-4" /> Cancel
                </button>
              </div>
            </>
          ) : (
            <p className="text-sm text-slate-500">No clear action found. Try a more specific instruction like “Replace Scene 2”.</p>
          )}
        </GlassCard>
      )}

      {status && status.status !== "idle" && (
        <GlassCard className="p-5" data-testid="ai-review-status">
          <div className="flex items-center gap-2 mb-3">
            {status.status === "running" ? <Loader2 className="w-4 h-4 animate-spin text-[#FFB300]" /> : <Check className="w-4 h-4 text-emerald-400" />}
            <span className="text-sm text-white">{status.status === "running" ? "Applying changes…" : "Changes applied"}</span>
          </div>
          <div className="space-y-1.5">
            {(status.results || []).map((r, i) => (
              <div key={i} className="text-xs flex items-center gap-2">
                {r.status === "done" ? <Check className="w-3.5 h-3.5 text-emerald-400" /> :
                  r.status === "skipped" ? <AlertTriangle className="w-3.5 h-3.5 text-slate-500" /> :
                    <X className="w-3.5 h-3.5 text-red-400" />}
                <span className={cx(r.status === "failed" ? "text-red-300" : "text-slate-300")}>{r.label}</span>
                {r.note && <span className="text-slate-600">· {r.note}</span>}
              </div>
            ))}
          </div>
        </GlassCard>
      )}
    </div>
  );
}
